import React from "react"
import PropTypes from "prop-types"
import { graphql, navigate } from "gatsby"
import Markdown from "markdown-to-jsx"
import SanityImage from "gatsby-plugin-sanity-image"
import CTAButton from "components/buttons/CTAButton"
import theme from "styles/theme"
import BlockContent from "components/BlockContent"
import useBreakpoints from "hooks/useBreakpoints"

const FeatureBlock = ({ label, title, copy, actions, image, context }) => {
  const { tablet, mobile } = useBreakpoints()
  const ctaObj = actions?.length ? actions.filter(({href}) => typeof href === 'string' && !!href.trim()).shift() : ''
  const ctaHref = typeof ctaObj === 'object' ? '' + ctaObj?.href || '' : ''
  return (
    <div
      css={[
        {
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
          minHeight: 515,
          margin: 10,
          [theme.tablet]: {
            display: "block",
          },
        },

        context.nthConsecutiveOfType % 2 === 0
          ? {
              backgroundColor: theme.deepBlue,
              "--shadowColor": "#016498",
              ".imageBlock": {
                order: 2,
              },
            }
          : {
              backgroundColor: theme.midBlue,
              "--shadowColor": theme.deepBlue,
            },
      ]}
    >
      <div
        css={{
          [theme.desktop]: {
            width: "100%",
            height: "100%",
            position: "relative",
          },
        }}
        className="imageBlock"
      >
        <SanityImage
          {...image}
          width={mobile ? 354 : tablet ? 747 : 710}
          height={mobile ? 283 : tablet ? 455 : 515}
          alt={title}
          onClick={e => ctaHref && navigate(ctaHref) || e.preventDefault()}
          css={{
            display: "block",
            width: "100%",
            objectFit: "cover",
            ...(ctaHref && {
              transition: theme.transitions.hoverDefault,
              cursor: 'pointer',
              ':hover': {opacity: .8}
            }),
            [theme.desktop]: {
              position: "absolute",
              height: "100%",
            },
          }}
        />
      </div>

      <div
        css={{
          padding: "110px 16.66%",
          color: theme.white,
          "& :last-child": {
            marginBottom: 0,
          },
          [theme.tablet]: {
            paddingTop: 64,
            paddingBottom: 64,
            textAlign: "center",
          },
          [theme.mobile]: {
            padding: "45px 8.47%",
          },
        }}
      >
        <div
          css={{
            ...theme.t5lg,
            color: theme.lightBlue30,
            marginBottom: 9,
            [theme.tablet]: { marginBottom: 12 },
            [theme.mobile]: { marginBottom: 10 },
          }}
        >
          {label}
        </div>

        <h3
          css={{
            ...theme.t3,
            ...(ctaHref && {
              transition: theme.transitions.hoverDefault,
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationThickness: '.07em',
              textUnderlineOffset: '0',
              textDecorationColor: `rgba(${theme.whiteRgbNums}, 0)`,
              ':hover': {
                textUnderlineOffset: '.145em',
                textDecorationColor: `rgba(${theme.whiteRgbNums}, .45)`,
              },
            }),
          }}
          onClick={e => ctaHref && navigate(ctaHref) || e.preventDefault()}
        >
          {title}
        </h3>

        <BlockContent
          css={{
            ...theme.bodyCopy,
            margin: "32px 0px 40px",
            a: { textDecoration: "underline" },
            [theme.tablet]: { margin: "20px 0 32px" },
            [theme.mobile]: { margin: "18px 0 30px" },
          }}
        >
          {copy}
        </BlockContent>

        {actions?.length > 0 && (
          <div
            css={[
              actions.length > 1 && {
                display: "inline-flex",
                flexDirection: "column",
              },
            ]}
          >
            {actions.map(({ _type, _key, ...actionProps }) => {
              actionProps.multi = actions.length > 1

              switch (_type) {
                case "ctaButton":
                  return <FeatureCTAButton key={_key} {...actionProps} />
                case "ctaCallout":
                  return <FeatureCallout key={_key} {...actionProps} />
                case "ctaIconButton":
                  return <FeatureIconButton key={_key} {...actionProps} />
                default:
                  return null
              }
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const FeatureCTAButton = ({ href, text, multi, ...props }) => (
  <CTAButton
    colorScheme="standard"
    size="md"
    href={href}
    css={[
      {
        "--shadowColor": "inherit",
      },
      multi && {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 10,
        minWidth: 320,
        [theme.tablet]: { minWidth: 284 },
        [theme.mobile]: { minWidth: 236 },
      },
    ]}
    {...props}
  >
    {text}
  </CTAButton>
)

FeatureCTAButton.propTypes = {
  href: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  multi: PropTypes.bool,
}

const FeatureCallout = ({ title, copy }) => (
  <div
    css={{
      ...theme.secondaryHighlight,
      marginBottom: 10,
      color: theme.lightOrange,
    }}
  >
    <h4
      css={{
        fontWeight: 500,
      }}
    >
      {title}
    </h4>

    {copy}
  </div>
)

FeatureCallout.propTypes = {
  title: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
}

const FeatureIconButton = ({ icon, ...rest }) => (
  <FeatureCTAButton
    {...rest}
    icon={<Markdown>{icon.asset.svg.content}</Markdown>}
  />
)

FeatureIconButton.propTypes = {
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  icon: PropTypes.shape({
    asset: PropTypes.shape({
      svg: PropTypes.shape({
        content: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}

FeatureBlock.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  copy: PropTypes.array.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      // Simple Button
      PropTypes.shape(FeatureCTAButton.propTypes),

      // Callout
      PropTypes.shape(FeatureCallout.propTypes),

      // Icon Button
      PropTypes.shape(FeatureIconButton.propTypes),
    ])
  ),
  image: PropTypes.shape({
    hotspot: PropTypes.object,
    crop: PropTypes.object,
    asset: PropTypes.object.isRequired,
  }).isRequired,
  context: PropTypes.shape({
    nthConsecutiveOfType: PropTypes.number.isRequired,
  }),
}

export default FeatureBlock

export const query = graphql`
  fragment FeatureBlock on SanityFeatureBlock {
    label
    title
    copy: _rawCopy
    image {
      ...Image
    }
    actions {
      ...CTAButton
      ...CTACallout
      ...CTAIconButton
    }
  }
`
